import { Box, HStack, Skeleton, useToast } from '@chakra-ui/react';
import * as _ from 'lodash';
import React, { useCallback, useEffect, useState } from 'react';

import { date as commonDate } from '@finley-technologies/common';

import {
  OrganizationGQLType,
  TAccountTypeGQLType,
  TransactionTypeGQLType,
} from '../../../../types/dashboard-core-gqlTypes.generated.d';
import { CsvDownloadButton } from 'packages/common-frontend/components/CsvDownloadButton';
import { FilterDatePicker } from 'packages/common-frontend/components/FilterDatePicker';
import { IsolatedMultiSelect } from 'packages/common-frontend/components/MultiSelect';
import { errorToastConfig } from 'packages/common-frontend/constants';
import { getUnexpectedErrorMessage } from 'packages/common-frontend/utils';
import { useGetCapitalProvidersQuery } from 'packages/organization/requests/organization.gql.generated';
import {
  useGetLedgerAccountsQuery,
  useGetLedgerQuery,
} from 'packages/statements/requests/ledger.gql.generated';
import {
  allLedgerTransactionTypes,
  formatLedgerTransactions,
  LedgerTransactionWithName,
  transactionTypeOptions,
} from 'packages/statements/ui/ledgerUtils';
import { OutstandingLedgerTable } from 'packages/statements/ui/OutstandingLedgerTable';

export const OutstandingLedgerTableContainer = ({
  accountTypes,
  capitalProviders,
  creditFacilityId,
}: {
  accountTypes: TAccountTypeGQLType[];
  capitalProviders: Pick<OrganizationGQLType, 'id' | 'name'>[];
  creditFacilityId: string;
}) => {
  const toast = useToast();
  const capitalProviderIds = _.map(capitalProviders, 'id');
  const capitalProviderOptions = _.map(capitalProviders, (capitalProvider) => ({
    value: capitalProvider.id,
    label: capitalProvider.name,
  }));

  // default: load past 30 days
  const defaultStartDate = commonDate
    .currentDateTime()
    .minus({ days: 30 })
    .toJSDate();
  const defaultEndDate = new Date();

  const [selectedTransactionTypes, setSelectedTransactionTypes] = useState<
    TransactionTypeGQLType[] | []
  >(allLedgerTransactionTypes);
  const [selectedCapitalProviders, setSelectedCapitalProviders] = useState<
    string[]
  >(capitalProviderIds);

  const [startDate, setStartDate] = useState<Date | null>(defaultStartDate);
  const [endDate, setEndDate] = useState<Date | null>(defaultEndDate);
  const [isDefaultFetch, setIsDefaultFetch] = useState<Boolean>(true);

  const {
    data: statementsData,
    loading: statementsLoading,
    refetch: refetchStatementsData,
  } = useGetLedgerQuery({
    /**
     *  Programmatically added onError handler.
     * @onError-TODO: Explicitly handle errors for this fetch
     */
    onError: (err) => {
      errorToastConfig.title = getUnexpectedErrorMessage(err);
      toast(errorToastConfig);
    },
    variables: {
      creditFacilityId,
      organizationIds: capitalProviderIds,
      selectedTransactionTypes: allLedgerTransactionTypes,
      accountTypes,
      startDate: startDate ? commonDate.dateToEpoch(startDate) : null,
      endDate: endDate ? commonDate.dateToEpoch(endDate) : null,
    },
    notifyOnNetworkStatusChange: true,
  });

  const refetchOutstandingLedgerDataHandler = useCallback(() => {
    refetchStatementsData({
      creditFacilityId,
      organizationIds: selectedCapitalProviders,
      selectedTransactionTypes,
      accountTypes,
      startDate: startDate ? commonDate.dateToEpoch(startDate) : null,
      endDate: endDate ? commonDate.dateToEpoch(endDate) : null,
    });
  }, [
    creditFacilityId,
    selectedCapitalProviders,
    selectedTransactionTypes,
    accountTypes,
    startDate,
    endDate,
    refetchStatementsData,
  ]);

  useEffect(() => {
    refetchOutstandingLedgerDataHandler();
  }, [startDate, endDate, refetchOutstandingLedgerDataHandler]);

  const statementsDataIsLoading = statementsLoading || !statementsData;

  if (statementsLoading || !statementsData) {
    return <></>;
  }

  // upon first fetch of statements data - if there are no transactions, try fetching without the start date limitation so that the latest outstanding balance can always be shown on initial load if it exists
  // this only applies to the initial fetch when the view is first loaded

  if (statementsData.ledger.length === 0 && isDefaultFetch) {
    refetchStatementsData({
      creditFacilityId,
      organizationIds: selectedCapitalProviders,
      selectedTransactionTypes,
      accountTypes,
      startDate: null,
      endDate: commonDate.dateToEpoch(defaultEndDate),
    });
    setStartDate(null);
  }
  if (isDefaultFetch) {
    setIsDefaultFetch(false);
  }

  const formattedTransactions = formatLedgerTransactions(
    capitalProviders,
    statementsData?.ledger,
  );

  const csvHeaders: {
    label: string;
    key: keyof LedgerTransactionWithName;
  }[] = [
    { label: 'Settlement Date', key: 'postingDate' },
    { label: 'Capital Provider', key: 'capitalProviderName' },
    { label: 'Transaction', key: 'transactionName' },
    { label: 'Amount', key: 'amount' },
    { label: 'Balance', key: 'balance' },
    { label: 'Daily Balance', key: 'dailyBalance' },
  ];

  const csvDataRows = _.map(formattedTransactions, (ledgerItem) => {
    const { amount, balance, type } = ledgerItem;
    if (type === 'debit') {
      return { ...ledgerItem, amount: -amount };
    }
    return {
      ...ledgerItem,
      amount: amount.toFixed(2),
      balance: balance.toFixed(2),
    };
  });

  return (
    <Box>
      <HStack spacing="50px" justify="space-between">
        <HStack spacing="50px">
          <HStack spacing="10px">
            <IsolatedMultiSelect
              applyButtonText="Apply filters"
              buttonProps={{ minWidth: '250px' }}
              counterLabel="Transactions"
              initialSelections={selectedTransactionTypes}
              isSearchable
              onApplyOptions={(val) => setSelectedTransactionTypes(val)}
              options={transactionTypeOptions}
              optionsHeader="Transactions"
              placeholderText="Select transactions"
              showSelectedCount
              titleText="All transactions"
            />
            <IsolatedMultiSelect
              applyButtonText="Apply filters"
              buttonProps={{ minWidth: '250px' }}
              counterLabel="Capital providers"
              initialSelections={selectedCapitalProviders}
              isSearchable
              onApplyOptions={(val) => setSelectedCapitalProviders(val)}
              options={capitalProviderOptions}
              optionsHeader="Capital providers"
              placeholderText="Select capital providers"
              showSelectedCount
              titleText="All capital providers"
            />
          </HStack>
          <FilterDatePicker
            startDate={startDate}
            endDate={endDate}
            setStartDate={setStartDate}
            setEndDate={setEndDate}
          />
        </HStack>
        <CsvDownloadButton
          dataRows={csvDataRows}
          headers={csvHeaders}
          filenamePrefix="outstanding-ledger"
          buttonLabel="Export CSV of current view"
          dataIsLoading={statementsDataIsLoading}
        />
      </HStack>
      <Box height="30px" />
      {statementsDataIsLoading ? (
        <Skeleton data-testid="loading-ledger" height="600px" />
      ) : (
        <OutstandingLedgerTable transactions={formattedTransactions} />
      )}
    </Box>
  );
};

export const OutstandingLedgerViewContainer = ({
  creditFacilityId,
}: {
  creditFacilityId: string;
}) => {
  const toast = useToast();
  const {
    data: ledgerAccountTypesData,
    loading: ledgerAccountTypesLoading,
  } = useGetLedgerAccountsQuery({
    /**
     *  Programmatically added onError handler.
     * @onError-TODO: Explicitly handle errors for this fetch
     */
    onError: (err) => {
      errorToastConfig.title = getUnexpectedErrorMessage(err);
      toast(errorToastConfig);
    },
    variables: {
      creditFacilityId,
      ledgerAccountGroup: 'outstandingLedger',
    },
  });
  const {
    data: capitalProvidersData,
    loading: capitalProvidersLoading,
  } = useGetCapitalProvidersQuery({
    /**
     *  Programmatically added onError handler.
     * @onError-TODO: Explicitly handle errors for this fetch
     */
    onError: (err) => {
      errorToastConfig.title = getUnexpectedErrorMessage(err);
      toast(errorToastConfig);
    },
    variables: {
      creditFacilityId,
    },
  });

  if (
    ledgerAccountTypesLoading ||
    !ledgerAccountTypesData ||
    !capitalProvidersData ||
    capitalProvidersLoading
  ) {
    return <></>;
  }

  const allAccountTypes = _.uniq(
    _.map(ledgerAccountTypesData.ledgerAccounts, 'tAccount'),
  );

  return (
    <Box>
      <OutstandingLedgerTableContainer
        accountTypes={allAccountTypes}
        capitalProviders={capitalProvidersData.capitalProviders}
        creditFacilityId={creditFacilityId}
      />
    </Box>
  );
};
